import React, { useContext, Fragment, useState, useRef, useCallback } from 'react';
import { withStyles } from '@material-ui/core/styles';
import ButtonBase from '@material-ui/core/ButtonBase';
import Badge from '@material-ui/core/Badge';
import Tooltip from '@material-ui/core/Tooltip';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import UpdateIcon from '@material-ui/icons/Update';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { AppContext, LayoutContext } from '../../common/contexts';
import {
    // INTERVIEW_STATUS_PENDING,
    INTERVIEW_STATUS_RESCHEDULE_JS,
    INTERVIEW_STATUS_RESCHEDULE_EMP,
    INTERVIEW_STATUS_DECLINED,
    INTERVIEW_STATUS_CONFIRMED,
    INTERVIEW_STATUS_CANCELLED,
} from '../../common/constants';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { getIsShowCalendarTileBatch } from '../../common/calendarHelper';
import { loadInterviewCard, loadInterviewNote } from '../../common/services/empMyCalendarServices';
import InterviewCard from './InterviewCard';
import AfitdTypography from '../../common/elemental/AfitdTypography';

import cx from 'clsx';
import { format } from 'date-fns';
import _get from 'lodash/get';

const styles = theme => ({
    root: {
        padding: '9px 8px 9px 8px',
        borderRadius: '10px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        [theme.breakpoints.down('sm')]: { padding: '0px', alignItems: 'center', justifyContent: 'center' },
    },
    badge: { width: '100%' },
    interviewContainer: { width: '100%', display: 'flex' },
    timeContainer: { flexGrow: 1, textAlign: 'left', alignItems: 'flex-start' },
    nameContainer: { textAlign: 'left', alignItems: 'flex-start' },
    declinedText: { textDecoration: 'line-through' },
    statusIconContainer: { flexGrow: 1, [theme.breakpoints.up('md')]: { textAlign: 'right' } },
    statusIconContainerCenter: { textAlign: 'center' },
    pendingIconContainer: { color: 'rgba(17, 30, 20, 0.4)' },
    rescheduleIconContainer: { color: '#FC821F' },
    confirmedIconContainer: { color: '#FC821F' },
    declinedIconContainer: { color: '#F0F2F6' },
    pending: {
        background: 'repeating-linear-gradient(60deg, rgba(0, 0, 0, 0.03), rgba(0, 0, 0, 0.03) 5px, #FFF 5px, #FFF 10px)',
        '&:hover': {
            background: 'repeating-linear-gradient(60deg, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.1) 5px, #FFF 5px, #FFF 10px)',
        },
    },
    pendingSelected: {
        background: 'repeating-linear-gradient(60deg, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.1) 5px, #FFF 5px, #FFF 10px)',
    },
    reschedule: {
        background: 'repeating-linear-gradient(60deg, rgba(125, 46, 2, 0.06), rgba(125, 46, 2, 0.06) 5px, #FFF 5px, #FFF 10px)',
        '&:hover': {
            background: 'repeating-linear-gradient(60deg, rgba(125, 46, 2, 0.1), rgba(125, 46, 2, 0.1) 5px, #FFF 5px, #FFF 10px)',
        },
    },
    rescheduleSelected: {
        background: 'repeating-linear-gradient(60deg, rgba(125, 46, 2, 0.1), rgba(125, 46, 2, 0.1) 5px, #FFF 5px, #FFF 10px)',
    },
    declined: {
        border: '1px solid #F0F2F6',
        backgroundColor: '#FFF',
        '&:hover': {
            border: '1px solid #D8DBE1',
            backgroundColor: '#FFF',
        },
    },
    declinedSelected: {
        border: '1px solid #D8DBE1',
        backgroundColor: '#FFF',
    },
    confirmed: {
        backgroundColor: '#FFF8E5',
        '&:hover': {
            backgroundColor: '#FFF1CB',
        },
    },
    confirmedSelected: {
        backgroundColor: '#FFF1CB',
    },
});

const ICON_STYLES = { width: '16px', height: '16px', marginTop: '2px' };

const getDefns = params => {
    const { props, employerId, isShowBatch, interviewCardAnchorEl, interviewCardData, interviewNote, interviewCardOpen } = params;
    const {
        setInterviewCardData,
        setInterviewNote,
        setSelectedDate,
        setInterviewCardOpen,
        setInterviewCardAnchorEl,
        setLoadInterviewCardUnsubscribeRef,
        setLoadInterviewNoteUnsubscribeRef,
        detachInterviewCardListener,
    } = params;
    const {
        classes,
        classes: {
            root: rootCls,
            statusIconContainer: statusIconContainerCls,
            statusIconContainerCenter: statusIconContainerCenterCls,
            pendingIconContainer: pendingIconContainerCls,
            rescheduleIconContainer: rescheduleIconContainerCls,
            confirmedIconContainer: confirmedIconContainerCls,
            declinedIconContainer: declinedIconContainerCls,
            pending: pendingCls,
            pendingSelected: pendingSelectedCls,
            reschedule: rescheduleCls,
            rescheduleSelected: rescheduleSelectedCls,
            declined: declinedCls,
            declinedSelected: declinedSelectedCls,
            confirmed: confirmedCls,
            confirmedSelected: confirmedSelectedCls,
            timeContainer: timeContainerCls,
            nameContainer: nameContainerCls,
            declinedText: declinedTextCls,
        },
        interviewData,
        dt,
        height,
        blockPercentage,
    } = props;

    const interviewId = _get(interviewData, 'interviewId');
    const isSelected = interviewCardOpen; //interviewId === clickedInterviewId;
    const status = _get(interviewData, 'status');
    const startDt = _get(interviewData, 'startDt');
    const startTime = startDt ? format(startDt.toDate(), 'h:mm') : '??:??';
    const startAmPm = startDt ? format(startDt.toDate(), 'a') : '??:??';
    const endDt = _get(interviewData, 'endDt');
    const endTime = endDt ? format(endDt.toDate(), 'h:mm') : '??:??';
    const amPm = endDt ? format(endDt.toDate(), 'a') : '??:??';
    const jobTitle = _get(interviewData, 'jobTitle');

    const isEmployer = !!employerId;

    let rootClasses = [rootCls];
    let statusIconContainerClasses = [statusIconContainerCls];
    let timeContainerClasses = [timeContainerCls];
    let nameContainerClasses = [nameContainerCls];
    let interviewBlockTooltipTitle = 'Interview Request';
    switch (status) {
        case INTERVIEW_STATUS_RESCHEDULE_JS:
        case INTERVIEW_STATUS_RESCHEDULE_EMP: {
            if (isSelected) {
                rootClasses.push(rescheduleSelectedCls);
            } else {
                rootClasses.push(rescheduleCls);
            }
            statusIconContainerClasses.push(rescheduleIconContainerCls);
            interviewBlockTooltipTitle = 'Reschedule Pending';
            break;
        }
        case INTERVIEW_STATUS_DECLINED:
        case INTERVIEW_STATUS_CANCELLED: {
            if (isSelected) {
                rootClasses.push(declinedSelectedCls);
            } else {
                rootClasses.push(declinedCls);
            }
            statusIconContainerClasses.push(declinedIconContainerCls);
            timeContainerClasses.push(declinedTextCls);
            nameContainerClasses.push(declinedTextCls);
            interviewBlockTooltipTitle = status === INTERVIEW_STATUS_DECLINED ? 'Interview Declined' : 'Interview Cancelled';
            break;
        }
        case INTERVIEW_STATUS_CONFIRMED: {
            if (isSelected) {
                rootClasses.push(confirmedSelectedCls);
            } else {
                rootClasses.push(confirmedCls);
            }
            statusIconContainerClasses.push(confirmedIconContainerCls);
            interviewBlockTooltipTitle = 'Interview Confirmed';
            break;
        }
        default: {
            // INTERVIEW_STATUS_PENDING
            if (isSelected) {
                rootClasses.push(pendingSelectedCls);
            } else {
                rootClasses.push(pendingCls);
            }
            statusIconContainerClasses.push(pendingIconContainerCls);
        }
    }

    if (height < 40 || blockPercentage < 51) {
        statusIconContainerClasses.push(statusIconContainerCenterCls);
    }

    let rootStylePadding = '9px 8px 9px 8px';
    if (blockPercentage < 51) {
        rootStylePadding = '0px 0px 0px 0px';
    } else if (height < 45) {
        rootStylePadding = '0px 0px 0px 8px';
    }

    let rootStyle = {
        height: height,
        width: '100%',
        padding: rootStylePadding,
        // alignItems: (height < 40 || blockPercentage < 50)? 'center' : 'flex-start',
        alignItems: blockPercentage < 51 ? 'center' : 'flex-start',
    };

    let defns = {
        root: {
            className: cx(rootClasses), // condition inside classnames didn't work for some reasons... so did it outside
            onClick: handleClickInterview.bind(
                null,
                isEmployer,
                interviewId,
                setInterviewCardAnchorEl,
                setInterviewCardData,
                setInterviewCardOpen,
                setInterviewNote,
                setSelectedDate.bind(null, dt),
                setLoadInterviewCardUnsubscribeRef,
                setLoadInterviewNoteUnsubscribeRef,
            ),
            style: rootStyle,
        },
        interviewBlockTooltip: {
            title: interviewBlockTooltipTitle,
            placement: 'top-end',
            arrow: true,
        },
        timeText: {
            variant: 'caption',
            value: `${startTime}${startAmPm.toLowerCase()}-${endTime}${amPm.toLowerCase()}`,
        },
        nameText: {
            variant: 'subtitle2',
            value: jobTitle,
        },
        statusIconContainer: {
            className: cx(statusIconContainerClasses),
        },
        timeContainer: {
            className: cx(timeContainerClasses),
        },
        nameContainer: {
            className: cx(nameContainerClasses),
        },
        interviewCard: {
            anchorEl: interviewCardAnchorEl,
            open: interviewCardOpen,
            data: interviewCardData,
            note: interviewNote,
            setInterviewCardOpen,
            setInterviewCardData,
            setInterviewNote,
            detachInterviewCardListener,
            onClose: handleCloseInterviewCard.bind(null, setInterviewCardOpen),
        },
        badge: {
            color: 'primary',
            badgeContent: ' ',
            variant: 'dot',
            invisible: !isShowBatch,
        },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

const getStatusIcon = props => {
    const status = _get(props, 'interviewData.status');
    switch (status) {
        case INTERVIEW_STATUS_RESCHEDULE_JS:
        case INTERVIEW_STATUS_RESCHEDULE_EMP: {
            return <UpdateIcon style={ICON_STYLES} />;
        }
        case INTERVIEW_STATUS_CONFIRMED: {
            return <CheckCircleIcon style={ICON_STYLES} />;
        }
        case INTERVIEW_STATUS_DECLINED:
        case INTERVIEW_STATUS_CANCELLED: {
            return <HighlightOffIcon style={ICON_STYLES} />;
        }
        default: {
            // INTERVIEW_STATUS_PENDING
            return <HelpOutlineIcon style={ICON_STYLES} />;
        }
    }
};

const handleClickInterview = (
    isEmployer,
    interviewId,
    setInterviewCardAnchorEl,
    setInterviewCardData,
    setInterviewCardOpen,
    setInterviewNote,
    setSelectedDate,
    setLoadInterviewCardUnsubscribeRef,
    setLoadInterviewNoteUnsubscribeRef,
    evt,
) => {
    evt.preventDefault();
    setInterviewCardAnchorEl(evt.target);
    // setClickedInterviewId(interviewId); //('dWIZrhfHLzF2xkodwVy1');
    const unsubscribe = loadInterviewCard(interviewId, setInterviewCardData, setInterviewCardOpen);
    const unsubscribeLoadInterviewNote = loadInterviewNote({ isEmployer, interviewId, setInterviewNote });

    setLoadInterviewCardUnsubscribeRef(unsubscribe);
    setLoadInterviewNoteUnsubscribeRef(unsubscribeLoadInterviewNote);
    setSelectedDate();
};

const handleCloseInterviewCard = setInterviewCardOpen => {
    setInterviewCardOpen(false);
};

const InterviewBlock = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const employerId = _get(appContext, 'employerId');
    const context = useContext(LayoutContext);
    const setSelectedDate = _get(context, 'setSelectedDate');
    const dashboardData = _get(context, 'dashboardData');
    const loadInterviewCardUnsubscribeRef = useRef();
    const loadInterviewNoteUnsubscribeRef = useRef();
    const theme = useTheme();
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [interviewCardAnchorEl, setInterviewCardAnchorEl] = useState(null);
    const [interviewCardData, setInterviewCardData] = useState();
    const [interviewNote, setInterviewNote] = useState();
    const [interviewCardOpen, setInterviewCardOpen] = useState(false);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const isHidden = _get(props, 'isHidden');
    const height = _get(props, 'height');
    const blockPercentage = _get(props, 'blockPercentage');
    const interviewId = _get(props, 'interviewData.interviewId');

    //---------------------------------------------------------------------
    // useCallback hook
    //---------------------------------------------------------------------
    const setLoadInterviewCardUnsubscribeRef = useCallback(unsubscribe => {
        loadInterviewCardUnsubscribeRef.current = unsubscribe;
    }, []);
    const setLoadInterviewNoteUnsubscribeRef = useCallback(unsubscribe => {
        loadInterviewNoteUnsubscribeRef.current = unsubscribe;
    }, []);
    const detachInterviewCardListener = useCallback(() => {
        if (loadInterviewCardUnsubscribeRef.current) {
            // call unsubscribe
            loadInterviewCardUnsubscribeRef.current();
        }
        if (loadInterviewNoteUnsubscribeRef.current) {
            // call unsubscribe
            loadInterviewNoteUnsubscribeRef.current();
        }
    }, []);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const isShowBatch = getIsShowCalendarTileBatch({ interviewId, dashboardData });

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const defns = getDefns({
        props,
        employerId,
        isShowBatch,
        interviewCardAnchorEl,
        setInterviewCardAnchorEl,
        interviewCardData,
        setInterviewCardData,
        interviewNote,
        setInterviewNote,
        setSelectedDate,
        interviewCardOpen,
        setInterviewCardOpen,
        setLoadInterviewCardUnsubscribeRef,
        setLoadInterviewNoteUnsubscribeRef,
        detachInterviewCardListener,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    if (isHidden === true) {
        return null;
    }

    if (blockPercentage < 51) {
        return (
            <Fragment>
                <Tooltip {...defns.interviewBlockTooltip}>
                    <Badge {...defns.badge}>
                        <ButtonBase {...defns.root}>
                            <div {...defns.interviewContainer}>
                                <div {...defns.statusIconContainer}>{getStatusIcon(props)}</div>
                            </div>
                        </ButtonBase>
                    </Badge>
                </Tooltip>
                {interviewCardOpen && <InterviewCard {...defns.interviewCard} />}
            </Fragment>
        );
    } else if (height && height < 40) {
        return (
            <Fragment>
                <Tooltip {...defns.interviewBlockTooltip}>
                    <Badge {...defns.badge}>
                        <ButtonBase {...defns.root}>
                            <div {...defns.interviewContainer}>
                                {smMatches ? null : (
                                    <div {...defns.nameContainer}>
                                        <AfitdTypography {...defns.nameText} value={defns.nameText.value + ' ' + defns.timeText.value} />
                                    </div>
                                )}
                                <div {...defns.statusIconContainer}>{getStatusIcon(props)}</div>
                            </div>
                        </ButtonBase>
                    </Badge>
                </Tooltip>
                {interviewCardOpen && <InterviewCard {...defns.interviewCard} />}
            </Fragment>
        );
    } else {
        // The interview block is high enough to hold the contents as usual
        return (
            <Fragment>
                <Tooltip {...defns.interviewBlockTooltip}>
                    <Badge {...defns.badge}>
                        <ButtonBase {...defns.root}>
                            <div {...defns.interviewContainer}>
                                {smMatches ? null : (
                                    <div {...defns.nameContainer}>
                                        <AfitdTypography {...defns.nameText} />
                                    </div>
                                )}
                                <div {...defns.statusIconContainer}>{getStatusIcon(props)}</div>
                            </div>
                            {smMatches ? null : (
                                <div {...defns.timeContainer}>
                                    <AfitdTypography {...defns.timeText} />
                                </div>
                            )}
                        </ButtonBase>
                    </Badge>
                </Tooltip>
                {interviewCardOpen && <InterviewCard {...defns.interviewCard} />}
            </Fragment>
        );
    }
};

export default withStyles(styles)(InterviewBlock);
